import { getHouseUuid, HOUSE_UUID_HEADER } from "./identity";
import type { ErrorCode } from "./types";

function resolveApiBaseUrl(): string {
    const configured = (import.meta.env.VITE_API_BASE_URL ?? "").trim();
    if (configured) {
        return configured;
    }

    if (typeof window !== "undefined") {
        const { protocol, hostname } = window.location;

        // Tauri desktop builds commonly serve the bundled frontend from an
        // internal origin such as `tauri://localhost` or `https://tauri.localhost`.
        // In those cases relative `/api/...` requests hit the desktop shell
        // instead of the remote game backend and return index.html.
        const isDesktopShellOrigin =
            !protocol.startsWith("http")
            || hostname === "tauri.localhost"
            || hostname.endsWith(".tauri.localhost");

        if (isDesktopShellOrigin) {
            return "https://rpg.tagdog.io";
        }
    }

    return "";
}

const apiBaseUrl = resolveApiBaseUrl();

interface Envelope<T> {
    message: string;
    data: T;
}

export interface ApiErrorBody {
    code?: ErrorCode | string;
    message?: string;
    details?: Record<string, unknown>;
}

export class ApiError extends Error {
    public readonly code?: ErrorCode | string;
    public readonly details?: Record<string, unknown>;
    public readonly status: number;
    public readonly path: string;

    constructor(status: number, path: string, body: ApiErrorBody) {
        super(body.message ?? `[${status}] ${path}`);
        this.name = "ApiError";
        this.status = status;
        this.path = path;
        this.code = body.code;
        this.details = body.details;
    }
}

function mergeHeaders(init?: RequestInit): RequestInit {
    const headers = new Headers(init?.headers);
    const uuid = getHouseUuid();
    if (uuid && !headers.has(HOUSE_UUID_HEADER)) {
        headers.set(HOUSE_UUID_HEADER, uuid);
    }
    if (init?.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }
    return { ...init, headers };
}

function unwrap<T>(raw: Envelope<T> | T): T {
    if (raw && typeof raw === "object" && "data" in (raw as object)) {
        return (raw as Envelope<T>).data;
    }
    return raw as T;
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${apiBaseUrl}${path}`, mergeHeaders(init));
    if (!response.ok) {
        let body: ApiErrorBody = {};
        try {
            body = await response.json() as ApiErrorBody;
        } catch {
            // non-JSON body
        }
        throw new ApiError(response.status, path, body);
    }
    const json = await response.json() as Envelope<T> | T;
    return unwrap(json);
}

/** GET 편의 메서드. */
export function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
    return apiRequest<T>(path, { ...init, method: "GET" });
}

/** JSON body POST. */
export function apiPost<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    return apiRequest<T>(path, {
        ...init,
        method: "POST",
        body: JSON.stringify(body ?? {}),
    });
}

/** JSON body PUT. */
export function apiPut<T>(path: string, body: unknown, init?: RequestInit): Promise<T> {
    return apiRequest<T>(path, {
        ...init,
        method: "PUT",
        body: JSON.stringify(body ?? {}),
    });
}

export function apiDelete<T>(path: string, init?: RequestInit): Promise<T> {
    return apiRequest<T>(path, { ...init, method: "DELETE" });
}
